import React, { useCallback } from 'react';
import { useFormikContext } from 'formik';
import { FormikErrors, FormikTouched } from 'formik';
import get from 'lodash/get';

import { useTranslation } from '@lyearn/i18n';
import { AlertCircleFill } from '@lyearn/icons';
import { Box, Stack, Tooltip, Typography, TypographyColors } from '@lyearn/ui';
import { Step, Stepper } from '@/components/MultiStep';

import { MultiStepSection } from '../types';

export interface MultiStepSidebarProps {
  sections: MultiStepSection[];
  disabledSections?: MultiStepSection['id'][];
  className?: string;
}

export const countSectionErrors = (
  section: MultiStepSection,
  errors: FormikErrors<any>,
  touched: FormikTouched<any>,
) => {
  const layoutsOfSection = section.sectionLayout.map((layout) => layout.layout || []);
  const fields = layoutsOfSection.flat(3);

  return fields.reduce((count, field) => {
    const hasError = get(errors, field) && get(touched, field);
    return hasError ? count + 1 : count;
  }, 0);
};

const getIconColor = (isActive: boolean, error: boolean) => {
  const iconColor = isActive ? 'icon-active' : 'icon-secondary';
  return error ? 'icon-error' : iconColor;
};

const getTextColor = (isActive: boolean, error: boolean) => {
  const textColor = isActive ? 'text-text-primary' : 'text-text-secondary';
  return (error ? 'text-text-error' : textColor) as TypographyColors;
};

const MultiStepSidebar: React.FC<MultiStepSidebarProps> = ({ sections, disabledSections }) => {
  const { t } = useTranslation();
  const { errors, touched } = useFormikContext();
  const renderError = useCallback(
    (id: string) => {
      return (
        <Tooltip placement="right" title={t('Common:Form.MandatoryFieldsMissing')}>
          <Box>
            <AlertCircleFill className="h-18 w-18" fillColorToken="icon-error" />
          </Box>
        </Tooltip>
      );
    },
    [t],
  );

  return (
    <Stepper
      className="sticky top-0 h-full w-[30rem] flex-shrink-0 self-start overflow-auto overflow-y-auto border-r border-divider px-20 pt-12 pb-32"
      direction="col">
      {sections.map((section, index) => {
        const error = countSectionErrors(section, errors, touched) > 0;

        return (
          <Step
            key={section.id}
            activeClassName="bg-bg-tertiary"
            alignItems="center"
            className="h-46 w-full rounded-8 px-12"
            disabled={disabledSections?.includes(section.id)}
            error={error}
            errorRenderer={renderError}
            id={section.id}
            justifyContent="between">
            {({ isActive }) => (
              <Stack alignItems="center" gapX={8}>
                {section.Icon ? (
                  <section.Icon
                    className="h-20 w-20"
                    fillColorToken={getIconColor(isActive, error)}
                  />
                ) : (
                  <Stack className="h-20 w-20 flex-shrink-0" />
                )}
                <Typography color={getTextColor(isActive, error)} variant="body-short-02">
                  {section.name}
                </Typography>
              </Stack>
            )}
          </Step>
        );
      })}
    </Stepper>
  );
};

export default MultiStepSidebar;
